import React, { FC, ReactNode } from 'react'
import type { RiskLibraryTaskDetail } from '../../core/_models'
import {
  likelihoodBadge,
  niceEnum,
  riskFactorBadge,
  sectionCardStyle,
  severityBadge,
} from './riskLibraryTaskDetailDisplay'

export type RiskLibraryTaskDetailReadOnlyViewProps = {
  detail: RiskLibraryTaskDetail
  /** e.g. library page “Edit task” row */
  topBar?: ReactNode
}

export const RiskLibraryTaskDetailReadOnlyView: FC<RiskLibraryTaskDetailReadOnlyViewProps> = ({ detail, topBar }) => (
  <>
    {topBar}
    {detail.categoryCode === 'CRITICAL_EQUIPMENT' ? (
      <p>
        <strong>Equipment:</strong> {detail.equipmentName || detail.title || '—'}
      </p>
    ) : (
      <p>
        <strong>Work activity:</strong> {detail.workActivity || detail.title}
      </p>
    )}
    {detail.categoryCode === 'CRITICAL_EQUIPMENT' ? (
      <>
        {(detail.failures || []).map((f, i) => (
          <div key={f.id ?? i} className='border rounded p-3 mb-3' style={sectionCardStyle(i, 'failure')}>
            <div className='fw-bold mb-2'>Failure: {f.failureText}</div>
            <div className='mb-2'>
              <div className='text-muted small'>Hazard</div>
              <ul className='mb-0'>
                {(f.hazardPoints || []).map((p, pi) => (
                  <li key={p.id ?? pi}>{p.pointText}</li>
                ))}
              </ul>
            </div>
            <div className='d-flex flex-wrap gap-1 mb-2'>
              <span className={`badge ${likelihoodBadge(f.likelihood)}`}>Likelihood: {niceEnum(f.likelihood)}</span>
              <span className={`badge ${severityBadge(f.severity)}`}>Severity: {niceEnum(f.severity)}</span>
              <span className={`badge ${riskFactorBadge(f.riskFactor)}`}>Risk: {niceEnum(f.riskFactor)}</span>
            </div>
            <div className='mb-2'>
              <div className='text-muted small'>Existing control measures</div>
              <ul className='mb-0'>
                {(f.existingControls || []).map((c) => (
                  <li key={c.id}>{c.lineText}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className='text-muted small'>Further control measures</div>
              <ul className='mb-0'>
                {(f.furtherControls || []).map((c) => (
                  <li key={c.id}>{c.lineText}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
        {!detail.failures?.length && <div className='text-muted'>No failure rows loaded yet for this task.</div>}
      </>
    ) : (
      <>
        {(detail.hazards ?? []).map((h, i) => (
          <div key={h.id ?? i} className='border rounded p-3 mb-3' style={sectionCardStyle(i, 'hazard')}>
            <div className='fw-bold mb-2'>{h.hazardText}</div>
            <div className='d-flex flex-wrap gap-1 mb-2'>
              <span className={`badge ${likelihoodBadge(h.likelihood)}`}>Likelihood: {niceEnum(h.likelihood)}</span>
              <span className={`badge ${severityBadge(h.severity)}`}>Severity: {niceEnum(h.severity)}</span>
              <span className={`badge ${riskFactorBadge(h.riskFactor)}`}>Risk: {niceEnum(h.riskFactor)}</span>
            </div>
            <div className='mb-2'>
              <div className='text-muted small'>Existing control measures</div>
              <ul className='mb-0'>
                {(h.existingControls || []).map((c) => (
                  <li key={c.id}>{c.lineText}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className='text-muted small'>Further control measures</div>
              <ul className='mb-0'>
                {(h.furtherControls || []).map((c) => (
                  <li key={c.id}>{c.lineText}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
        {!detail.hazards?.length && <div className='text-muted'>No hazard rows loaded yet for this task.</div>}
      </>
    )}
  </>
)
